import { useTranslation } from "react-i18next"

import { ArrowUpRight, FileSearch } from "@/components/icons"
import { cn } from "@/lib/utils"

import { WebSourceIcon } from "./web-source-icon"
import { buttonVariants } from "../../../components/ui/button-variants"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip"
import { resolveWebCitationDisplay } from "../utils/web-citation-display"

type UrlCitationItemProps = {
  citation: string
  index: number
  onNavigateToInline: (index: number) => void
  onShowChunk?: (citation: string) => void
  hasChunk?: boolean
  isHighlighted?: boolean
  messageIndex?: number
}

export function UrlCitationItem({
  citation,
  index,
  onNavigateToInline,
  onShowChunk,
  hasChunk = false,
  isHighlighted = false,
  messageIndex,
}: UrlCitationItemProps) {
  const { t } = useTranslation()

  const { cleanUrl, hostname, normalizedUrl } = resolveWebCitationDisplay(citation)

  return (
    <div
      data-citation-index={index}
      data-message-index={messageIndex}
      role="presentation"
      className={cn(
        "border-border/50 bg-muted/30 hover:border-primary/30 flex items-center gap-2 rounded-md border p-2 text-sm transition-colors",
        "cursor-pointer",
        isHighlighted && "animate-[highlight_2s_ease-in-out]"
      )}
      // Mouse convenience for the whole row; keyboard users activate the inner button,
      // whose click bubbles up to this handler.
      onClick={() => {
        onNavigateToInline(index)
      }}
    >
      <button
        className="focus-visible:ring-ring flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-sm text-left focus-visible:ring-1 focus-visible:outline-none"
        type="button"
      >
        <span className="text-muted-foreground ml-1 shrink-0 text-xs font-medium">
          {index + 1}.
        </span>
        <WebSourceIcon hostname={hostname} />
        <span className="text-foreground flex min-w-0 flex-1 items-center gap-2" title={cleanUrl}>
          <span className="text-foreground shrink-0 text-xs">{hostname}</span>
          <span className="text-muted-foreground line-clamp-1 truncate text-xs">{cleanUrl}</span>
        </span>
      </button>
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={normalizedUrl}
              rel="noopener noreferrer"
              target="_blank"
              className={cn(
                buttonVariants({
                  size: "icon",
                  variant: "ghost",
                }),
                "h-6 w-6 p-0"
              )}
              onClick={(event) => {
                event.stopPropagation()
              }}
            >
              <ArrowUpRight className="size-4" />
            </a>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
            <p>{t("answer.show_document")}</p>
          </TooltipContent>
        </Tooltip>
        {hasChunk && onShowChunk && (
          <Tooltip>
            <TooltipTrigger
              className={cn(
                buttonVariants({
                  size: "icon",
                  variant: "ghost",
                }),
                "h-6 w-6 p-0"
              )}
              onClick={(e) => {
                e.stopPropagation()
                onShowChunk(normalizedUrl)
              }}
            >
              <FileSearch className="size-4" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
              <p>{t("answer.show_chunk")}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
